/**
 * Ahrefs Target Normalization (Story 20.1, AC #1, #12)
 *
 * `normalizeAhrefsTarget` accepts the customer's raw input (a domain or URL)
 * and returns a canonical lowercase form suitable for Ahrefs `target=` query
 * params. Validation mirrors the SSRF-safe rules from PageSpeed:
 *
 *  - Strip `http(s)://` if present
 *  - Lowercase the hostname
 *  - Strip a trailing slash
 *  - Reject localhost / private IP ranges / `.local` / `metadata.google.internal`
 *  - Reject IPv6 literals
 *  - Reject non-standard ports
 *
 * `inferAhrefsMode` picks the correct `mode=` parameter for the target. v3
 * requires `mode` on every `/site-explorer/*` call (no default), so we infer
 * sensibly:
 *
 *  - bare hostname (no path) → `mode=domain`
 *  - URL with a path component → `mode=exact_url`
 *
 * Customers can override via the widget config (deferred to Story 20.4).
 *
 * The default-mode-vs-override decision is documented in `_bmad/bmm/data/
 * integration-metrics/ahrefs.md` Implementation Notes section.
 */

import { sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Types
// ============================================================================

export type AhrefsMode = "domain" | "subdomain" | "prefix" | "exact_url";

export class AhrefsTargetValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AhrefsTargetValidationError";
  }
}

// ============================================================================
// SSRF-safe hostname rules (mirrors PageSpeed)
// ============================================================================

/**
 * Returns true if the hostname is forbidden (private, internal, metadata, etc.).
 * Mirrors the PageSpeed validator rules so the same SSRF defenses apply.
 */
function isBlockedHostname(host: string): boolean {
  const h = host.toLowerCase();
  if (h === "localhost" || h === "127.0.0.1" || h === "::1") return true;
  if (/^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.|169\.254\.)/.test(h)) return true;
  if (h.endsWith(".local") || h.endsWith(".internal") || h.endsWith(".svc.cluster.local")) {
    return true;
  }
  if (h === "metadata.google.internal") return true;
  if (h.startsWith("[")) return true; // IPv6 literal in URL form
  return false;
}

// ============================================================================
// Normalization (AC #1)
// ============================================================================

/**
 * Normalize a raw customer-supplied target (domain or URL) into the canonical
 * form Ahrefs expects.
 *
 * @param input - Raw user input (e.g. "Example.com", "https://example.com/",
 *   "https://example.com/blog/seo-tips").
 * @returns Canonical form: lowercase hostname, trailing slash stripped,
 *   path preserved when present.
 * @throws AhrefsTargetValidationError if the input is empty, malformed, or
 *   matches an SSRF-forbidden hostname.
 */
export function normalizeAhrefsTarget(input: string): string {
  if (typeof input !== "string") {
    throw new AhrefsTargetValidationError("Target must be a string.");
  }
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    throw new AhrefsTargetValidationError("Target cannot be empty.");
  }
  if (trimmed.length > 2048) {
    throw new AhrefsTargetValidationError("Target must not exceed 2048 characters.");
  }

  // Add a synthetic protocol if the user pasted a bare domain so URL parsing
  // doesn't choke. We strip the protocol back out at the end if it was synthetic.
  const wasBare = !/^https?:\/\//i.test(trimmed);
  const candidate = wasBare ? `https://${trimmed}` : trimmed;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new AhrefsTargetValidationError(
      `Invalid target: ${sanitizeErrorMessage(trimmed)}`
    );
  }

  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new AhrefsTargetValidationError(
      "Target protocol must be http or https."
    );
  }

  const hostname = parsed.hostname.toLowerCase();
  if (hostname.length === 0 || !hostname.includes(".")) {
    throw new AhrefsTargetValidationError(
      "Target must include a top-level domain."
    );
  }
  if (isBlockedHostname(hostname)) {
    throw new AhrefsTargetValidationError(
      "Target points to a private, internal, or metadata host."
    );
  }
  if (parsed.port && parsed.port !== "80" && parsed.port !== "443") {
    throw new AhrefsTargetValidationError(
      "Target uses a non-standard port — Ahrefs only indexes default-port hosts."
    );
  }

  parsed.hostname = hostname;
  let normalized = parsed.toString();

  // For bare-domain inputs, drop the synthetic protocol and any trailing slash
  // so the canonical form is the bare host (e.g. "example.com").
  if (wasBare) {
    // URL.toString() always emits a trailing slash on root-only URLs, drop it
    normalized = `${hostname}${parsed.pathname === "/" ? "" : parsed.pathname}${parsed.search}`;
  }

  // Strip trailing slash on URLs with a non-root path
  if (normalized.endsWith("/") && !normalized.endsWith("://")) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
}

// ============================================================================
// Mode inference (AC #12)
// ============================================================================

/**
 * Infer the Ahrefs `mode` parameter from a normalized target.
 *
 * Heuristic:
 *  - Bare hostname OR root-path URL → `mode=domain` (covers `example.com`,
 *    `https://example.com`, `https://example.com/`)
 *  - URL with a non-root path → `mode=exact_url` (covers
 *    `https://example.com/blog/seo-tips`)
 *
 * Subdomain and prefix modes are reserved for explicit user override via
 * widget config (deferred to Story 20.4).
 *
 * @param normalizedTarget - Output of `normalizeAhrefsTarget`.
 * @returns The default mode for the target.
 */
export function inferAhrefsMode(normalizedTarget: string): AhrefsMode {
  // If the input has a protocol, parse it to inspect the path.
  if (/^https?:\/\//i.test(normalizedTarget)) {
    try {
      const parsed = new URL(normalizedTarget);
      if (parsed.pathname && parsed.pathname !== "/" && parsed.pathname.length > 0) {
        return "exact_url";
      }
      return "domain";
    } catch {
      return "domain";
    }
  }
  // Bare-domain form (e.g. "example.com" or "example.com/path")
  if (normalizedTarget.includes("/")) {
    return "exact_url";
  }
  return "domain";
}
